import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Image, X, Trash2, Send, Phone, Mail, MessageCircle, Clock, Megaphone, Heart, Video } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow, differenceInDays } from 'date-fns';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MarketplacePage({ user }) {
  const [ads, setAds] = useState([]);
  const [newAd, setNewAd] = useState('');
  const [adImage, setAdImage] = useState(null);
  const [adImagePreview, setAdImagePreview] = useState(null);
  const [adVideo, setAdVideo] = useState(null);
  const [adVideoPreview, setAdVideoPreview] = useState(null);
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchAds(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/ads`);
      setAds(response.data);
    } catch (error) {
      toast.error('Failed to load ads');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setAdImage(file);
    setAdImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setAdImage(null);
    if (adImagePreview) URL.revokeObjectURL(adImagePreview);
    setAdImagePreview(null);
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) { toast.error('Please select a video file'); return; }
    if (file.size > 50 * 1024 * 1024) { toast.error('Video must be less than 50MB'); return; }
    setAdVideo(file);
    setAdVideoPreview(URL.createObjectURL(file));
  };

  const removeAdVideo = () => {
    setAdVideo(null);
    if (adVideoPreview) URL.revokeObjectURL(adVideoPreview);
    setAdVideoPreview(null);
  };

  const handleCreateAd = async (e) => {
    e.preventDefault();
    if (!newAd.trim()) return;

    setLoading(true);
    try {
      let imageUrl = null;
      let videoUrl = null;

      if (adImage) {
        const formData = new FormData();
        formData.append('file', adImage);
        const uploadRes = await axios.post(`${API}/upload/ad-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadRes.data.image_url;
      }

      if (adVideo) {
        const formData = new FormData();
        formData.append('file', adVideo);
        const uploadRes = await axios.post(`${API}/upload/video`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        videoUrl = uploadRes.data.video_url;
      }

      await axios.post(`${API}/ads`, {
        content: newAd,
        image: imageUrl,
        video: videoUrl,
        contact_phone: contactPhone,
        contact_email: contactEmail
      });
      setNewAd('');
      setContactPhone('');
      setContactEmail('');
      removeImage();
      removeAdVideo();
      fetchAds();
      toast.success('Ad posted! It will be visible for 7 days.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ad');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Delete this ad?')) return;
    try {
      await axios.delete(`${API}/ads/${adId}`);
      fetchAds();
      toast.success('Ad deleted');
    } catch (error) {
      toast.error('Failed to delete ad');
    }
  };

  const handleLike = async (adId) => {
    try {
      await axios.post(`${API}/ads/${adId}/like`);
      fetchAds();
    } catch (error) {
      toast.error('Failed to like ad');
    }
  };

  const getDaysRemaining = (createdAt) => {
    const days = 7 - differenceInDays(new Date(), new Date(createdAt));
    return Math.max(0, days);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="marketplace-page">
      <div className="flex items-center gap-3 mb-8">
        <Megaphone className="w-8 h-8 text-[#cb9944]" />
        <div>
          <h1 className="text-4xl font-bold text-[#2d3748]">Marketplace</h1>
          <p className="text-[#64748b] text-sm">Advertise your services — ads expire after 7 days</p>
        </div>
      </div>

      {/* Create Ad */}
      <Card className="border-gray-100 rounded-2xl shadow-sm mb-8" data-testid="create-ad-card">
        <CardContent className="pt-6">
          <form onSubmit={handleCreateAd}>
            <div className="flex items-start gap-3 mb-4">
              <Avatar>
                <AvatarImage src={user?.profile_image ? getImageUrl(user.profile_image) : undefined} />
                <AvatarFallback className="bg-[#cb9944] text-white">
                  {(user?.display_name || user?.full_name || '?').charAt(0)}
                </AvatarFallback>
              </Avatar>
              <Textarea
                value={newAd}
                onChange={(e) => setNewAd(e.target.value)}
                placeholder="Describe your service or product..."
                className="flex-1 bg-gray-50 border-gray-200 rounded-xl min-h-[80px] resize-none"
                data-testid="new-ad-textarea"
              />
            </div>

            {/* Contact Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#5b7f95] flex-shrink-0" />
                <input
                  type="text"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="Contact phone (optional)"
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-[#5b7f95] focus:ring-2 focus:ring-[#5b7f95]/20 outline-none"
                  data-testid="ad-phone-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#5b7f95] flex-shrink-0" />
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="Contact email (optional)"
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-[#5b7f95] focus:ring-2 focus:ring-[#5b7f95]/20 outline-none"
                  data-testid="ad-email-input"
                />
              </div>
            </div>

            {/* Image Preview */}
            {adImagePreview && (
              <div className="relative mb-4 inline-block">
                <img src={adImagePreview} alt="Preview" className="h-32 rounded-xl object-cover" />
                <button type="button" onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* Video Preview */}
            {adVideoPreview && (
              <div className="relative mb-4">
                <video src={adVideoPreview} className="max-h-40 rounded-xl" controls />
                <button type="button" onClick={removeAdVideo}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <label className="cursor-pointer">
                  <div className="flex items-center gap-2 text-[#5b7f95] hover:text-[#4a6b80] transition-colors">
                    <Image className="w-5 h-5" />
                    <span className="text-sm">Image</span>
                  </div>
                  <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                </label>
                <label className="cursor-pointer">
                  <div className="flex items-center gap-2 text-[#5b7f95] hover:text-[#4a6b80] transition-colors">
                    <Video className="w-5 h-5" />
                    <span className="text-sm">Video</span>
                  </div>
                  <input type="file" accept="video/*" onChange={handleVideoSelect} className="hidden" />
                </label>
              </div>
              <Button
                type="submit"
                disabled={loading || !newAd.trim()}
                className="bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full"
                data-testid="create-ad-btn"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                {loading ? 'Posting...' : 'Post Ad'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Ads Feed */}
      <div className="space-y-6">
        {ads.map((ad) => {
          const daysLeft = getDaysRemaining(ad.created_at);
          return (
            <Card key={ad.id} className="border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow" data-testid="ad-card">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={ad.user_profile_image ? getImageUrl(ad.user_profile_image) : undefined} />
                      <AvatarFallback className="bg-[#cb9944] text-white">
                        {(ad.user_name || '?').charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <Link
                        to={`/directory?member=${ad.user_id}`}
                        className="font-semibold text-[#2d3748] hover:text-[#5b7f95] transition-colors cursor-pointer"
                        data-testid="ad-author-link"
                      >
                        {ad.user_name}
                      </Link>
                      {ad.user_business_name && (
                        <p className="text-sm text-[#5b7f95] font-medium">{ad.user_business_name}</p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-[#64748b]">
                        <span>{formatDistanceToNow(new Date(ad.created_at), { addSuffix: true })}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                        </span>
                      </div>
                    </div>
                  </div>
                  {(ad.user_id === user.id || user.is_admin) && (
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(ad.id)}
                      className="text-red-500 hover:text-red-600" data-testid="delete-ad-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-[#2d3748] mb-4 whitespace-pre-wrap">{ad.content}</p>

                {ad.image && (
                  <div className="mb-4 rounded-xl overflow-hidden bg-gray-100">
                    <img src={getImageUrl(ad.image)} alt="Ad" className="w-full max-h-[400px] object-contain" />
                  </div>
                )}

                {ad.video && (
                  <div className="mb-4 rounded-xl overflow-hidden bg-gray-100">
                    <video src={ad.video} controls className="w-full max-h-[400px]" preload="metadata" data-testid="ad-video" />
                  </div>
                )}

                {/* Contact Details */}
                <div className="bg-[#f0f5f8] rounded-xl p-4 space-y-2 mb-4">
                  {ad.contact_phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-[#5b7f95]" />
                      <a href={`tel:${ad.contact_phone}`} className="text-[#2d3748] hover:text-[#5b7f95]">{ad.contact_phone}</a>
                    </div>
                  )}
                  {ad.contact_email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-[#5b7f95]" />
                      <a href={`mailto:${ad.contact_email}`} className="text-[#2d3748] hover:text-[#5b7f95]">{ad.contact_email}</a>
                    </div>
                  )}
                </div>

                {/* Like & Actions */}
                <div className="flex items-center gap-4 mb-4 pt-2 border-t border-gray-100">
                  <Button variant="ghost" size="sm" onClick={() => handleLike(ad.id)}
                    className={`rounded-full ${(ad.likes || []).includes(user.id) ? 'text-red-500' : 'text-gray-600'}`}
                    data-testid="like-ad-btn">
                    <Heart className="w-4 h-4 mr-1" fill={(ad.likes || []).includes(user.id) ? 'currentColor' : 'none'} />
                    {(ad.likes || []).length}
                  </Button>
                </div>

                {/* Message Button */}
                {ad.user_id !== user.id && (
                  <Link to={`/messages/${ad.user_id}`}>
                    <Button className="w-full bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full" data-testid="message-advertiser-btn">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Message {ad.user_name?.split(' ')[0]}
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          );
        })}

        {ads.length === 0 && (
          <div className="text-center py-12 text-[#64748b]">
            <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No ads yet. Be the first to advertise your services!</p>
          </div>
        )}
      </div>
    </div>
  );
}
